// ---------------------------------------------------------------
// Idle-time tracking.
//  - "stopped" = under 2 mph
//  - we track how long since the vehicle last moved (stopped duration)
//  - idle counting starts once it's been stopped WITH the engine on for
//    more than 5 minutes (a grace period so red lights don't count)
// Accumulates while the dashboard is open; on the server it runs 24/7.
// ---------------------------------------------------------------

const KEY = 'dd-idle';
const GRACE_SEC = 300; // 5-minute grace before idle counts

export function getIdle() {
  try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch { return {}; }
}
function save(d) { localStorage.setItem(KEY, JSON.stringify(d)); }

export function fmtIdle(sec) {
  const s = Math.round(sec || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

export function idleForDay(deviceId, dayLabel, todayLabel) {
  const st = getIdle()[deviceId];
  if (!st) return 0;
  if (dayLabel === todayLabel) return st.todaySec || 0;
  return (st.history && st.history[dayLabel]) || 0;
}

export function resetIdle(id) {
  const d = getIdle();
  if (d[id]) { d[id].totalSec = 0; d[id].todaySec = 0; d[id].stoppedOnSec = 0; d[id].alertedHours = 0; d[id].lastMovedTs = Date.now(); save(d); }
}

export function accumulateIdle(devices, positions) {
  const data = getIdle();
  const now = Date.now();
  const today = new Date().toDateString();
  const alerts = [];

  devices.forEach((d) => {
    const p = positions.find((x) => x.deviceId === d.id);
    if (!p) return;
    const a = p.attributes || {};
    // "Idling" means the engine is genuinely RUNNING while stopped. The ignition
    // flag alone is unreliable (it can stay stuck "on" after the car is off), so
    // we also require a real running signal: RPM actually spinning, or the
    // alternator charging (voltage ≥ 13.2 V). A parked/off car — RPM 0 and
    // voltage settling below 13.2 V — no longer counts as idling.
    // RPM is the authoritative "engine running" signal — 0 vs a few hundred is
    // unambiguous. Voltage is a backup, but only trusted inside a sane charging
    // window (13.2–15.5 V) so a raw/unscaled reading like 46 can't be mistaken
    // for "charging" and re-trigger false idle.
    const rpm = Number(a.io36) || 0;
    const volts = Number(a.power) || 0;
    const charging = volts >= 13.2 && volts <= 15.5;
    const engineOn = a.ignition === true && (rpm > 400 || charging);
    const mph = (p.speed || 0) * 1.15078;
    const moving = mph >= 2;

    const st = data[d.id] || {
      totalSec: 0, todaySec: 0, day: today, history: {},
      lastTs: now, lastMovedTs: now, stoppedOnSec: 0, alertedHours: 0, name: d.name,
    };
    st.name = d.name;
    if (!st.history) st.history = {};
    if (st.day !== today) { st.history[st.day] = Math.round(st.todaySec); st.day = today; st.todaySec = 0; }

    let dt = (now - (st.lastTs || now)) / 1000;
    if (dt < 0 || dt > 300) dt = 0; // ignore gaps (app was closed)

    if (moving) {
      st.lastMovedTs = now;
      st.stoppedOnSec = 0;
      st.alertedHours = 0;
      st.idlingNow = false;
    } else if (engineOn) {
      st.stoppedOnSec = (st.stoppedOnSec || 0) + dt;
      if (st.stoppedOnSec > GRACE_SEC) {
        // beyond the 5-min grace → count as idle
        st.totalSec += dt;
        st.todaySec += dt;
        st.idlingNow = true;
        const idleSec = st.stoppedOnSec - GRACE_SEC;
        const hrs = Math.floor(idleSec / 3600);
        if (hrs > (st.alertedHours || 0)) {
          st.alertedHours = hrs;
          const stoppedMin = Math.round((now - st.lastMovedTs) / 60000);
          alerts.push({
            key: `idle:${d.id}:${today}:${hrs}`, entity: d.name, severity: 'warn', icon: '🕒',
            title: `Idling ${hrs} hour${hrs > 1 ? 's' : ''}`,
            info: `${d.name} has been idling ${hrs} hour${hrs > 1 ? 's' : ''} — stopped with the engine on for ${stoppedMin} min. Wasting fuel.`,
          });
        }
      } else {
        st.idlingNow = false; // still within the 5-min grace
      }
    } else {
      // stopped with engine off
      st.stoppedOnSec = 0;
      st.alertedHours = 0;
      st.idlingNow = false;
    }

    st.engineOn = engineOn;
    st.moving = moving;
    st.stoppedSec = moving ? 0 : Math.round((now - st.lastMovedTs) / 1000); // since last movement
    st.lastTs = now;
    data[d.id] = st;
  });

  save(data);
  return alerts;
}
